import type { BlackboardChannel, BlackboardInstance } from "./blackboardModels";
import type { OrgChannelSummary } from "./useOrgChannels";

export interface ActorFleetSharedGroup {
  actors: BlackboardInstance[];
  channel: BlackboardChannel;
  kind: "shared";
  summary: OrgChannelSummary | null;
}

export interface ActorFleetUnassignedGroup {
  actors: BlackboardInstance[];
  kind: "unassigned";
}

export type ActorFleetGroup = ActorFleetSharedGroup | ActorFleetUnassignedGroup;

export function buildActorChannelGroups(
  instances: readonly BlackboardInstance[],
  orgChannelsById: ReadonlyMap<string, OrgChannelSummary>,
): ActorFleetGroup[] {
  const sharedGroups = new Map<string, ActorFleetSharedGroup>();
  const unassignedActors: BlackboardInstance[] = [];

  for (const instance of instances) {
    const sharedChannel = instance.channels.find(
      (channel) => channel.kind === "shared" && !channel.archivedAt,
    );
    if (!sharedChannel) {
      unassignedActors.push(instance);
      continue;
    }
    const existing = sharedGroups.get(sharedChannel.id);
    if (existing) {
      existing.actors.push(instance);
      continue;
    }
    sharedGroups.set(sharedChannel.id, {
      actors: [instance],
      channel: sharedChannel,
      kind: "shared",
      summary: orgChannelsById.get(sharedChannel.id) ?? null,
    });
  }

  const sortedSharedGroups = [...sharedGroups.values()]
    .map((group) => ({
      ...group,
      actors: sortActorsByStableCreationOrder(group.actors),
    }))
    .toSorted((left, right) => {
      const memberDelta = right.actors.length - left.actors.length;
      if (memberDelta !== 0) {
        return memberDelta;
      }
      return groupDisplayName(left).localeCompare(groupDisplayName(right));
    });

  if (unassignedActors.length === 0) {
    return sortedSharedGroups;
  }

  return [
    ...sortedSharedGroups,
    {
      actors: sortActorsByStableCreationOrder(unassignedActors),
      kind: "unassigned",
    },
  ];
}

function sortActorsByStableCreationOrder(
  actors: readonly BlackboardInstance[],
): BlackboardInstance[] {
  return [...actors].toSorted((left, right) => {
    const createdAtDelta = left.createdAt.localeCompare(right.createdAt);
    if (createdAtDelta !== 0) {
      return createdAtDelta;
    }
    return left.id.localeCompare(right.id);
  });
}

function groupDisplayName(group: ActorFleetSharedGroup): string {
  return group.summary?.displayName ?? group.channel.displayName;
}
